class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-product-form]');
    if (!this.form) return;

    this.variantInput = this.form.querySelector('input[name="id"]');
    if (this.variantInput) this.variantInput.disabled = false;

    this.submitButton = this.form.querySelector('[type="submit"]');
    if (this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');

    this.cartDrawer = document.querySelector('cart-drawer');
    this.errorContainer = this.querySelector('[data-form-error]');

    this.form.addEventListener('submit', (evt) => this._onSubmitHandler(evt));
    this._initVariantSubscriber();
  }

  disconnectedCallback() {
    this.unsubscribeVariant?.();
  }

  _initVariantSubscriber() {
    if (typeof subscribe === 'undefined') return;

    // Forms whose variant is chosen by their own UI (e.g. combo cards) opt out,
    // otherwise a PDP variant change would overwrite their hidden id input.
    if (this.hasAttribute('data-skip-variant-sync')) return;

    this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
      if (data.productId !== this.dataset.productId) return;

      const variantInput = this.form.querySelector('input[name="id"]');
      const buttonText = this.submitButton?.querySelector('[data-button-text]');

      if (data.variant) {
        if (variantInput) variantInput.value = data.variant.id;

        // capped-product-bogo (hard cap) can also be set per-variant via a Metaobject
        // rule (see variant-selector.liquid's bogo_capped_at_1), so it must be
        // re-evaluated on every variant switch, not just read once from the initial
        // page render. data-tag-capped-bogo (product-tag-driven, applies to every
        // variant) is a floor here — it must never be cleared by a variant that has
        // no rule of its own.
        const tagCappedBogo = this.hasAttribute('data-tag-capped-bogo');
        this.toggleAttribute('data-capped-bogo', tagCappedBogo || !!data.variant.bogo_capped_at_1);

        if (this.submitButton && buttonText) {
          const productAtcTitle = this.dataset.productAtcTitle?.trim() ?? '';
          // A custom ATC title worded like "sold out" contradicts a continue-selling
          // variant, which is still purchasable — ignore it and fall back to the
          // normal Add to cart label instead of misleading copy.
          const resolvedAtcTitle = (data.variant.atc_title?.trim() || productAtcTitle).trim();
          const isSoldOutWorded = ['sold out', 'sold-out', 'soldout'].includes(
            resolvedAtcTitle.toLowerCase()
          );

          if (productAtcTitle && data.variant.inventory_policy === 'continue' && !isSoldOutWorded) {
            this.submitButton.disabled = false;
            buttonText.textContent = resolvedAtcTitle;
          } else if (data.variant.available) {
            this.submitButton.disabled = false;
            buttonText.textContent = window.theme?.strings?.addToCart ?? 'Add to cart';
          } else {
            this.submitButton.disabled = true;
            buttonText.textContent = window.theme?.strings?.soldOut ?? 'Sold out';
          }
        }
      } else {
        if (this.submitButton && buttonText) {
          this.submitButton.disabled = true;
          buttonText.textContent = window.theme?.strings?.unavailable ?? 'Unavailable';
        }
      }
    });
  }

  async _onSubmitHandler(evt) {
    evt.preventDefault();
    if (this.submitButton?.disabled) return;

    if (await this._isEyePatchCappedBlocked()) {
      this._showEyePatchPopup();
      return;
    }

    if (await this._isCappedBogoBlocked()) {
      this._showCappedBogoPopup();
      return;
    }

    if (await this._isTypeCappedBlocked()) {
      const productType = (this.dataset.productType || '').trim().toLowerCase();
      const maxQty = Number(window.CappedBogoConfig?.cappedTypes?.[productType]) || 4;
      this._showTypeCappedPopup(maxQty);
      return;
    }

    this._setButtonLoading(true);
    this._hideError();

    this.cartDrawer?.openCart();

    const formData = new FormData(this.form);
    const sectionId = this.cartDrawer?.dataset?.sectionId ?? 'cart-drawer';
    formData.set('sections', sectionId);
    formData.set('sections_url', window.location.pathname);
    formData.set('properties[is_app]', 'true');

    try {
      const response = await CartAPI.add(formData);

      // Tell the site-wide capped BOGO guard about this add so every other add
      // path (boost popup, recommendations, quick-add …) blocks a re-add of it
      // in the same session without waiting for a page reload.
      if (this.hasAttribute('data-capped-bogo')) {
        window.CappedBogoGuard?.markCapped({
          productId: Number(this.dataset.productId),
          variantId: Number(this.form?.querySelector('input[name="id"]')?.value),
          tagCap: this.hasAttribute('data-tag-capped-bogo'),
        });
      }

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productId: this.dataset.productId });

      this.cartDrawer?.renderContents(response);
    } catch (error) {
      const message = error?.description ?? error?.message ?? 'Something went wrong.';
      this._showError(message);
      this._setSoldOut();
      publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', error });
    } finally {
      this._setButtonLoading(false);
    }
  }

  _setButtonLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;

    const btnText = this.submitButton.querySelector('[data-button-text]');
    const btnLoader = this.submitButton.querySelector('[data-button-loader]');

    if (btnText) btnText.classList.toggle('invisible', isLoading);
    if (btnLoader) {
      btnLoader.classList.toggle('hidden', !isLoading);
      btnLoader.classList.toggle('flex', isLoading);
    }

    const screenOverlay = document.querySelector('[data-atc-screen-overlay]');
    if (screenOverlay) screenOverlay.style.display = isLoading ? 'flex' : 'none';
  }

  _setSoldOut() {
    if (!this.submitButton) return;
    this.submitButton.disabled = true;
    const btnText = this.submitButton.querySelector('[data-button-text]');
    if (btnText) btnText.textContent = window.theme?.strings?.soldOut ?? 'Sold Out';
  }

  _showError(message) {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = message;
    this.errorContainer.hidden = false;
  }

  _hideError() {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = '';
    this.errorContainer.hidden = true;
  }

  // IP Sale "max 1 per cart" capping (cart_mode_price_set_for_ip metaobject:
  // product_capping / varriant_capping, plus two legacy hardcoded variant
  // IDs) — checked directly against the live variant id input rather than a
  // static data-attribute, so a variant switch on the PDP (no page reload)
  // is picked up correctly. Gated on #eye-patch-capping-on so this only
  // applies when the shop metafield custom.product_varinats_capping is on
  // (eye-patch-capping.liquid only renders that element, and the capped-id
  // config alongside it, in that case).
  static _getEyePatchCappedVariantIds() {
    if (ProductForm._eyePatchCappedIds !== undefined) return ProductForm._eyePatchCappedIds;

    if (!document.getElementById('eye-patch-capping-on')) {
      ProductForm._eyePatchCappedIds = [];
      return ProductForm._eyePatchCappedIds;
    }

    const ids = [41892488839333, 40325864751269];
    const configEl = document.getElementById('eye-patch-capping-config');
    if (configEl) {
      try {
        const data = JSON.parse(configEl.textContent);
        String(data.capVariantIds || '')
          .split(',')
          .forEach((raw) => {
            const id = Number(raw.trim());
            if (id > 0) ids.push(id);
          });
      } catch {
        // malformed config — fall back to the two hardcoded ids only
      }
    }
    ProductForm._eyePatchCappedIds = ids;
    return ids;
  }

  async _isEyePatchCappedBlocked() {
    const variantId = Number(this.form?.querySelector('input[name="id"]')?.value);
    if (!variantId) return false;
    if (!ProductForm._getEyePatchCappedVariantIds().includes(variantId)) return false;
    if (typeof CartAPI === 'undefined') return false;

    try {
      const cart = await CartAPI.get();
      return cart.items.some((item) => item.variant_id === variantId && item.quantity >= 1);
    } catch {
      return false; // fail open — same reasoning as _isCappedBogoBlocked below
    }
  }

  _showEyePatchPopup() {
    // Close-button wiring for .popup-for-eyepatch is handled by
    // eye-patch-capping.js's handlePopupClick, already loaded whenever this
    // popup markup exists — no need to duplicate it here.
    document.querySelector('.popup-for-eyepatch')?.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  // capped-product-bogo: the product (tag) or the selected variant (Metaobject
  // capped_at_1 rule) already carries a quantity in the cart, so this add must be
  // blocked rather than pushing the line to 2. Fails open (allows the add) if the
  // cart can't be read, since a false negative here is far less costly than silently
  // blocking every add on a transient network error.
  async _isCappedBogoBlocked() {
    if (!this.hasAttribute('data-capped-bogo')) return false;
    if (typeof CartAPI === 'undefined') return false;

    const productId = Number(this.dataset.productId);
    if (!productId) return false;

    try {
      const cart = await CartAPI.get();

      // Product-level tag caps the whole product (any variant already in cart blocks
      // a re-add of any variant); the variant-level Metaobject rule (capped_at_1) only
      // caps that one variant, so other variants of the same product stay addable.
      if (this.hasAttribute('data-tag-capped-bogo')) {
        return cart.items.some((item) => item.product_id === productId && item.quantity >= 1);
      }

      const variantId = Number(this.form?.querySelector('input[name="id"]')?.value);
      if (!variantId) return false;
      return cart.items.some((item) => item.variant_id === variantId && item.quantity >= 1);
    } catch {
      return false;
    }
  }

  _showCappedBogoPopup() {
    ProductForm._wireCappedBogoPopup();
    document.querySelector('.popup-for-capped-bogo')?.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  // Per-product-type BOGO quantity cap (window.CappedBogoConfig.cappedTypes,
  // seeded by snippets/capped-bogo-popup.liquid from the same
  // bogo_capped_product_type / bogo_capped_product_type_max_qty fields
  // bogo-script.js and capped-bogo-guard.js already read). Checked BEFORE
  // openCart()/CartAPI.add() in _onSubmitHandler — same reason as
  // _isCappedBogoBlocked() above — so the drawer never opens and the add
  // never happens for a product whose type is already at cap, instead of
  // capped-bogo-guard.js's optimistic add-then-correct handling (which still
  // covers every OTHER add path on the site: quick-add, bundles, etc., where
  // there's no equivalent pre-submit hook to check from).
  // data-product-type must be present (see the <product-form> snippets) for
  // this to do anything; missing/blank type or no CappedBogoConfig fails open.
  async _isTypeCappedBlocked() {
    const productType = (this.dataset.productType || '').trim().toLowerCase();
    if (!productType) return false;

    const cfg = window.CappedBogoConfig;
    const cappedTypes = cfg?.cappedTypes;
    if (!cappedTypes || !Object.prototype.hasOwnProperty.call(cappedTypes, productType)) return false;
    if (typeof CartAPI === 'undefined') return false;

    const maxQty = Number(cappedTypes[productType]) || 4;
    const requestedQty = Number(this.form?.querySelector('input[name="quantity"]')?.value) || 1;

    try {
      const cart = await CartAPI.get();
      const currentQty = cart.items
        .filter((item) => String(item.product_type || '').trim().toLowerCase() === productType)
        .reduce((sum, item) => sum + item.quantity, 0);
      return currentQty + requestedQty > maxQty;
    } catch {
      return false; // fail open — see _isCappedBogoBlocked's identical reasoning
    }
  }

  _showTypeCappedPopup(maxQty) {
    const message = `You can only add ${maxQty} of this product to your cart`;
    if (window.CappedBogoGuard?.showPopup) {
      window.CappedBogoGuard.showPopup(message);
      return;
    }
    // capped-bogo-guard.js hasn't loaded for some reason — fall back to the
    // shared popup element directly, without the dynamic message.
    this._showCappedBogoPopup();
  }

  // Delegated close handler, wired once regardless of how many <product-form> instances
  // connect on the page — the popup element itself is a single shared singleton in the DOM.
  static _wireCappedBogoPopup() {
    if (ProductForm._cappedBogoPopupWired) return;
    ProductForm._cappedBogoPopupWired = true;

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.popup-for-capped-bogo')) return;
      if (!event.target.closest('.popup-close, .popup-button')) return;

      document.querySelector('.popup-for-capped-bogo')?.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
    });
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
